import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import {
    MyPermissionViewModel,
    MyPermissionViewModelListResponseResult,
    UserProxy,
} from '../../proxy/proxies';
import { LocalStorageService } from './storage/local-storage.service';
import { CROPMAP_PERMISSION_MENU_ITEMS } from '../constants/cropmap-permissions.constants';

const PERMISSIONS_STORAGE_KEY = 'permissions';

@Injectable({
    providedIn: 'root',
})
export class PermissionService {
    permissions: MyPermissionViewModel[] = [];
    private readonly permissionSubject = new BehaviorSubject<MyPermissionViewModel[]>(
        inject(LocalStorageService).getItem<MyPermissionViewModel[]>(PERMISSIONS_STORAGE_KEY) ?? []
    );
    readonly permissions$ = this.permissionSubject.asObservable();

    private readonly userProxy = inject(UserProxy);
    private readonly localStorage = inject(LocalStorageService);

    /** Load permissions from API and cache (call after login / on app entry when token exists). */
    loadPermissions(): Observable<MyPermissionViewModelListResponseResult> {
        return this.userProxy.user_GetMyPermissions().pipe(
            tap((res) => {
                if (res?.result) {
                    this.setPermissions(res.result);
                }
            })
        );
    }

    loadPermissionsIfAuthenticated(): void {
        const token = this.localStorage.getItem<string>('token');
        if (!token) return;
        this.loadPermissions().subscribe({
            error: () => {
                /* optional: toast */
            },
        });
    }

    setPermissions(list: MyPermissionViewModel[]): void {
        this.permissions = list ?? [];
        this.permissionSubject.next(this.permissions);
        this.localStorage.setItem(PERMISSIONS_STORAGE_KEY, this.permissions);
    }

    clearPermissions(): void {
        this.permissions = [];
        this.permissionSubject.next([]);
        this.localStorage.removeItem(PERMISSIONS_STORAGE_KEY);
    }

    hasPermission(permission: string): boolean {
        const list =
            this.permissionSubject.value?.length > 0
                ? this.permissionSubject.value
                : (this.localStorage.getItem<MyPermissionViewModel[]>(PERMISSIONS_STORAGE_KEY) ?? []);
        if (!list?.length) return false;
        const normalized = permission.replace(/-/g, '').toLowerCase();
        return list.some(
            (e) =>
                (e.code ?? '').replace(/-/g, '').toLowerCase() === normalized
        );
    }

    /** Same as {@link hasPermission} — aligns with Doktar-App-Web naming. */
    isGrantedOnUserPermissions(code: string): boolean {
        return this.hasPermission(code);
    }

    /**
     * First route the user may open based on `_get` permissions and {@link CROPMAP_PERMISSION_MENU_ITEMS}.
     * Falls back to `/plantation` (CropMap default).
     */
    getFirstAccessiblePage(): string {
        const list =
            this.permissionSubject.value?.length > 0
                ? this.permissionSubject.value
                : (this.localStorage.getItem<MyPermissionViewModel[]>(PERMISSIONS_STORAGE_KEY) ?? []);
        if (!list?.length) return '/plantation';

        for (const { routerLink, permissionKey } of CROPMAP_PERMISSION_MENU_ITEMS) {
            const code = `${permissionKey}_get`.toLowerCase();
            const has = list.some((p) => (p.code ?? '').toLowerCase() === code);
            if (has) {
                return routerLink.startsWith('/') ? routerLink : `/${routerLink}`;
            }
        }

        for (const p of list) {
            const permissionCode = (p.code ?? '').toLowerCase();
            if (!permissionCode.includes('permission') && permissionCode.endsWith('_get')) {
                const pageName = permissionCode.replace(/_get$/, '');
                const route = this.findRouteByPermission(pageName);
                if (route && route !== '/plantation') {
                    return route;
                }
            }
        }

        return '/plantation';
    }

    private findRouteByPermission(permissionName: string): string {
        const name = permissionName.replace(/-/g, '').toLowerCase();
        for (const item of CROPMAP_PERMISSION_MENU_ITEMS) {
            const key = item.permissionKey.toLowerCase().replace(/-/g, '');
            if (
                key === name ||
                key === name.replace(/s$/, '') ||
                name === key + 's'
            ) {
                const link = item.routerLink.startsWith('/') ? item.routerLink : `/${item.routerLink}`;
                return link;
            }
        }
        return '/plantation';
    }

    getPermissionByRoute(route: string): boolean {
        const normalized = route.startsWith('/') ? route : `/${route}`;
        const item = CROPMAP_PERMISSION_MENU_ITEMS.find((e) => {
            const link = e.routerLink.startsWith('/') ? e.routerLink : `/${e.routerLink}`;
            return link === normalized || normalized.startsWith(link + '/');
        });
        if (item) {
            return this.hasPermission(`${item.permissionKey}_get`);
        }
        return true;
    }
}
